/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * client when the rule names this client and can be edited here; client_type when it covers every client of this type; all_clients when it covers every client. Rules that do not name this client are shown for context and edited elsewhere.
 */
export const AuthClientAudienceEntryAppliesTo = {
  Client: "client",
  ClientType: "client_type",
  AllClients: "all_clients",
} as const;
/**
 * client when the rule names this client and can be edited here; client_type when it covers every client of this type; all_clients when it covers every client. Rules that do not name this client are shown for context and edited elsewhere.
 */
export type AuthClientAudienceEntryAppliesTo = OpenEnum<
  typeof AuthClientAudienceEntryAppliesTo
>;

/**
 * use grants mcp:auth on the client; blocked writes mcp:blocked_auth, which outranks grants.
 */
export const AuthClientAudienceEntryLevel = {
  Use: "use",
  Blocked: "blocked",
} as const;
/**
 * use grants mcp:auth on the client; blocked writes mcp:blocked_auth, which outranks grants.
 */
export type AuthClientAudienceEntryLevel = OpenEnum<
  typeof AuthClientAudienceEntryLevel
>;

/**
 * One rule deciding who may use an auth client. The rule applies wherever the client is used, on every server that offers it.
 */
export type AuthClientAudienceEntry = {
  /**
   * client when the rule names this client and can be edited here; client_type when it covers every client of this type; all_clients when it covers every client. Rules that do not name this client are shown for context and edited elsewhere.
   */
  appliesTo: AuthClientAudienceEntryAppliesTo;
  /**
   * For personal clients: how the rule relates to other people's connections. A grant never lets anyone use another user's connection.
   */
  connectionNote?: string | undefined;
  /**
   * use grants mcp:auth on the client; blocked writes mcp:blocked_auth, which outranks grants.
   */
  level: AuthClientAudienceEntryLevel;
  /**
   * The principal the rule names: user:<id>, user:all, role:<id> or agent:<id>.
   */
  principalUrn: string;
  /**
   * The project the rule is confined to, when it is.
   */
  projectId?: string | undefined;
};

/** @internal */
export const AuthClientAudienceEntryAppliesTo$inboundSchema: z.ZodMiniType<
  AuthClientAudienceEntryAppliesTo,
  unknown
> = openEnums.inboundSchema(AuthClientAudienceEntryAppliesTo);

/** @internal */
export const AuthClientAudienceEntryLevel$inboundSchema: z.ZodMiniType<
  AuthClientAudienceEntryLevel,
  unknown
> = openEnums.inboundSchema(AuthClientAudienceEntryLevel);

/** @internal */
export const AuthClientAudienceEntry$inboundSchema: z.ZodMiniType<
  AuthClientAudienceEntry,
  unknown
> = z.pipe(
  z.object({
    applies_to: AuthClientAudienceEntryAppliesTo$inboundSchema,
    connection_note: z.optional(z.string()),
    level: AuthClientAudienceEntryLevel$inboundSchema,
    principal_urn: z.string(),
    project_id: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "applies_to": "appliesTo",
      "connection_note": "connectionNote",
      "principal_urn": "principalUrn",
      "project_id": "projectId",
    });
  }),
);

export function authClientAudienceEntryFromJSON(
  jsonString: string,
): SafeParseResult<AuthClientAudienceEntry, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AuthClientAudienceEntry$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AuthClientAudienceEntry' from JSON`,
  );
}
